import { useEffect, useState } from 'react';
import { faLockKeyhole } from '@fortawesome/pro-regular-svg-icons';

import { ModalProps, Secret } from '@typings';
import { getSecrets } from '@services';
import { clusterContextSelector, contextNamesSelector } from '@selectors';
import { useFetchList, useSelector } from '@hooks';
import { as, cn, noop, path } from '@utils';

import { Button, Icon, Modal, Table } from '@components';

type Props = ModalProps & {
  onApply?: (field: { path: string; prefix?: string }) => void;
};

export const SchemaVolumeSecretModal = ({
  onApply = noop,
  closeModal = noop,
}: Props) => {
  const cluster = useSelector(clusterContextSelector);
  const { organizationName, projectName } = useSelector(contextNamesSelector);

  const { list: secrets, getList } = useFetchList<Secret[]>({
    getList: getSecrets,
    fetchOnMount: false,
  });

  const [activeSecret, setActiveSecret] = useState('');

  const { secretsUrl } = as.c(cluster);

  useEffect(() => {
    const getSecretList = async () => {
      if (projectName) {
        await getList({ secretsUrl, organizationName, projectName });
      }
    };

    getSecretList();
  }, [organizationName, projectName, secretsUrl, getList]);

  const handleApply = () => {
    if (activeSecret) {
      onApply({
        path: `/${activeSecret}`,
        prefix: path.create(organizationName, projectName),
      });
      closeModal();
    }
  };

  const makeSecret = ({ key }: Secret) => {
    const isActive = key === activeSecret;

    return (
      <Table.Row key={key} onClick={() => setActiveSecret(key)}>
        <Table.Cell
          className={cn(
            'cursor-pointer border-neural-01 py-2 transition-colors hover:bg-neural-01',
            {
              '!bg-neural-02': isActive,
            },
          )}
        >
          <div className="flex items-center gap-2">
            <Icon icon={faLockKeyhole} /> {key}
          </div>
        </Table.Cell>
      </Table.Row>
    );
  };

  return (
    <Modal.Content title="Choose a Secret Path" className="w-[720px]">
      <Table className="h-[240px] w-full auto-rows-min grid-cols-1 overflow-auto bg-background">
        <Table.Header>
          <Table.Row>
            <Table.Head className="bg-background">Name</Table.Head>
          </Table.Row>
        </Table.Header>
        <Table.Body>{secrets.map(makeSecret)}</Table.Body>
      </Table>
      <Modal.Footer>
        <Button
          disabled={!activeSecret}
          className="px-14"
          onClick={handleApply}
        >
          Apply
        </Button>
      </Modal.Footer>
    </Modal.Content>
  );
};
